import { useMemo } from 'react';
import {
  Home, BookOpen, FolderKanban, BarChart3, Layers, Sparkles,
} from 'lucide-react';
import { useAuthentication } from '../../context/AuthContext';
import VerityLayout, { VerityNavItem } from './VerityLayout';
import VerityOverview from './VerityOverview';
import StandardsLibrary from './StandardsLibrary/StandardsLibrary';
import AwpRuleLibrary from './Awp/AwpRuleLibrary';
import AwpDashboard from './Awp/AwpDashboard';
import Projects from './Projects/Projects';
import Dashboard from './Dashboard/Dashboard';
import ComingSoon from './components/ComingSoon';
import { useNavTab } from './components/useNavTab';
import { useReviewMode } from './components/useReviewMode';

const NAV: VerityNavItem[] = [
  { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  // Document AI, Compliance Engine, Findings and Audit Trail are no longer nav items — they are the
  // stages of a project, reached by the step tracker inside one.
  { key: 'projects', label: 'Projects', icon: FolderKanban },
  { key: 'standards', label: 'Standards Library', icon: BookOpen },
  { key: 'overview', label: 'Overview', icon: Home, footer: true },
  { key: 'architecture', label: 'Architecture', icon: Layers, footer: true },
  { key: 'case-study', label: 'Case Study', icon: Sparkles, footer: true },
];

const SOON: Record<string, { title: string }> = {
  architecture: { title: 'Platform Architecture' },
  'case-study': { title: 'Case Study — Viking CCS' },
};

/** User-side IQ Verity module — the IQ Verity shell with the Overview reproduced. */
const VerityUser = () => {
  // In the URL, so a hard refresh stays where you were and a tab can be linked to. See useNavTab.
  const [active, setActive] = useNavTab('dashboard', NAV.map((n) => n.key));
  const [mode, setMode] = useReviewMode();
  const { user } = useAuthentication();

  const userName = useMemo(
    () => user?.name || user?.displayName || user?.email || 'Reviewer',
    [user]
  );

  const activeItem = NAV.find((n) => n.key === active) ?? NAV[0];

  return (
    <VerityLayout
      navItems={NAV}
      active={active}
      onNavigate={setActive}
      activeLabel={activeItem.label}
      userName={userName}
      mode={mode}
      onModeChange={setMode}
    >
      {active === 'overview' && <VerityOverview onNavigate={setActive} />}
      {active === 'standards' && mode === 'engineering' && <StandardsLibrary key={mode} readOnly reviewType={mode} />}
      {active === 'standards' && mode === 'awp' && <AwpRuleLibrary readOnly />}
      {/* Keyed on the mode so switching starts the list fresh rather than flashing the other side's rows. */}
      {active === "projects" && <Projects key={mode} reviewType={mode} />}
      {/* The dashboard counts engineering deliverables and findings. The AWP one is not built yet. */}
      {active === "dashboard" && mode === "engineering" && <Dashboard />}
      {active === "dashboard" && mode === "awp" && <AwpDashboard />}
      {active !== "overview" && active !== "standards" && active !== "projects" && active !== "dashboard" && (
        <ComingSoon icon={activeItem.icon} title={SOON[active]?.title ?? activeItem.label} />
      )}
    </VerityLayout>
  );
};

export default VerityUser;
